import React, { useState, useEffect } from 'react';
import { Table, Button, Input, DatePicker, Tag, Card, message, Spin, Space } from 'antd';
import { SearchOutlined, ReloadOutlined } from '@ant-design/icons';
import { api } from '../../contexts/AuthContext';
import dayjs from 'dayjs';

const { RangePicker } = DatePicker;

const OperationLogs = () => {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [pagination, setPagination] = useState({ current: 1, pageSize: 20, total: 0 });
  const [searchText, setSearchText] = useState('');
  const [dateRange, setDateRange] = useState(null);
  const [operationType, setOperationType] = useState('');

  const operationTypes = [
    { value: '', label: '全部类型' },
    { value: '激活用户', label: '激活用户' },
    { value: '修改积分', label: '修改积分' },
    { value: '编辑用户', label: '编辑用户' },
    { value: '删除用户', label: '删除用户' },
    { value: '审核订单', label: '审核订单' },
    { value: '创建管理员', label: '创建管理员' },
  ];

  const fetchLogs = async (page = 1, pageSize = 20) => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      params.set('page', page);
      params.set('page_size', pageSize);
      
      if (searchText) params.set('search', searchText);
      if (operationType) params.set('type', operationType);
      if (dateRange && dateRange.length === 2) {
        params.set('start_date', dateRange[0].format('YYYY-MM-DD'));
        params.set('end_date', dateRange[1].format('YYYY-MM-DD'));
      }

      const response = await api.get(`/admin/logs?${params.toString()}`);
      setLogs(response.data.logs);
      setPagination({
        current: response.data.page,
        pageSize: response.data.page_size,
        total: response.data.total,
      });
    } catch (error) {
      message.error('获取操作日志失败');
      console.error('获取操作日志失败:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchLogs(pagination.current, pagination.pageSize);
  }, []);

  const handleSearch = () => {
    fetchLogs(1, pagination.pageSize);
  };

  const handleReset = () => {
    setSearchText('');
    setDateRange(null);
    setOperationType('');
    fetchLogs(1, pagination.pageSize);
  };

  const handleTableChange = (pagination) => {
    fetchLogs(pagination.current, pagination.pageSize);
  };

  const columns = [
    {
      title: '操作人',
      dataIndex: 'operator_name',
      key: 'operator_name',
      width: 120,
      render: (name, record) => (
        <div>
          <div>{name}</div>
          <div style={{ fontSize: 12, color: '#999' }}>
            {record.operator_level === 'super' && '超级管理员'}
            {record.operator_level === 'senior' && '高级管理员'}
            {record.operator_level === 'regular' && '普通管理员'}
          </div>
        </div>
      ),
    },
    {
      title: '操作类型',
      dataIndex: 'operation_type',
      key: 'operation_type',
      width: 120,
      render: (type) => (
        <Tag color="blue">{type}</Tag>
      ),
    },
    {
      title: '操作模块',
      dataIndex: 'operation_module',
      key: 'operation_module',
      width: 100,
      render: (module) => (
        <Tag color="green">{module}</Tag>
      ),
    },
    {
      title: '目标',
      dataIndex: 'target_name',
      key: 'target_name',
      width: 120,
      render: (name, record) => (
        name || (record.target_id ? `ID: ${record.target_id}` : '-')
      ),
    },
    {
      title: '操作描述',
      dataIndex: 'description',
      key: 'description',
      ellipsis: true,
    },
    {
      title: 'IP地址',
      dataIndex: 'ip_address',
      key: 'ip_address',
      width: 150,
      render: (ip) => ip || '-',
    },
    {
      title: '状态',
      dataIndex: 'success',
      key: 'success',
      width: 80,
      render: (success) => (
        <Tag color={success ? 'success' : 'error'}>
          {success ? '成功' : '失败'}
        </Tag>
      ),
    },
    {
      title: '操作时间',
      dataIndex: 'created_at',
      key: 'created_at',
      width: 170,
      render: (time) => {
        if (!time) return '-';
        return dayjs(time).format('YYYY-MM-DD HH:mm:ss');
      },
    },
  ];

  return (
    <Card
      title="操作日志"
      extra={
        <Button icon={<ReloadOutlined />} onClick={() => fetchLogs(pagination.current, pagination.pageSize)}>
          刷新
        </Button>
      }
    >
      <div style={{ marginBottom: 16, display: 'flex', gap: 16, flexWrap: 'wrap' }}>
        <Input
          placeholder="搜索操作人、目标或描述"
          prefix={<SearchOutlined />}
          value={searchText}
          onChange={(e) => setSearchText(e.target.value)}
          onPressEnter={handleSearch}
          style={{ width: 250 }}
        />
        <Input
          placeholder="操作类型"
          value={operationType}
          onChange={(e) => setOperationType(e.target.value)}
          style={{ width: 150 }}
        />
        <RangePicker
          value={dateRange}
          onChange={(dates) => setDateRange(dates)}
          placeholder={['开始日期', '结束日期']}
          format="YYYY-MM-DD"
        />
        <Space>
          <Button type="primary" onClick={handleSearch}>
            搜索
          </Button>
          <Button onClick={handleReset}>
            重置
          </Button>
        </Space>
      </div>
      <Spin spinning={loading}>
        <Table
          columns={columns}
          dataSource={logs}
          rowKey="id"
          pagination={pagination}
          onChange={handleTableChange}
          scroll={{ x: 1000 }}
        />
      </Spin>
    </Card>
  );
};

export default OperationLogs;